/**
 * React hooks for Web Worker–backed computation.
 *
 * useMonteCarloWorker — offloads Monte Carlo simulation to a background thread.
 * useExcelWorker      — offloads workbook generation (non-chart portion).
 */

import { useState, useCallback, useEffect, useRef } from "react";
import type { MonteCarloResult } from "../model";
import type { McWorkerRequest, McWorkerResponse } from "./types";
import type { ExcelWorkerRequest, ExcelWorkerResponse } from "./excel.worker";

/* ── Monte Carlo hook ── */

export interface UseMonteCarloWorkerReturn {
  /** Start a simulation. Terminates any previous run. */
  run: (
    scenario: unknown,
    enabledKeys: string[],
    ranges: Record<string, number> | undefined,
    iterations: number,
    onResult: (result: MonteCarloResult) => void,
  ) => void;
  /** Whether a simulation is currently running. */
  running: boolean;
  /** Completed iterations so far (0 … iterations). */
  progress: number;
  /** Most recent worker failure, cleared when a new run starts. */
  error: string | null;
  /** Terminate the current worker immediately. */
  terminate: () => void;
}

export function useMonteCarloWorker(): UseMonteCarloWorkerReturn {
  const workerRef = useRef<Worker | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (workerRef.current) {
        workerRef.current.onmessage = null;
        workerRef.current.onerror = null;
        workerRef.current.terminate();
      }
      workerRef.current = null;
    };
  }, []);

  const run = useCallback(
    (
      scenario: unknown,
      enabledKeys: string[],
      ranges: Record<string, number> | undefined,
      iterations: number,
      onResult: (result: MonteCarloResult) => void,
    ) => {
      if (workerRef.current) {
        workerRef.current.onmessage = null;
        workerRef.current.onerror = null;
        workerRef.current.terminate();
      }

      const worker = new Worker(
        new URL("./monte-carlo.worker.ts", import.meta.url),
        { type: "module" },
      );

      worker.onmessage = (e: MessageEvent<McWorkerResponse>) => {
        if (workerRef.current !== worker) return;
        if (e.data.type === "MONTE_CARLO_PROGRESS") {
          if (mountedRef.current) setProgress(e.data.completed);
        } else if (e.data.type === "MONTE_CARLO_RESULT") {
          worker.onmessage = null;
          worker.onerror = null;
          if (mountedRef.current) {
            onResult(e.data.result);
            setRunning(false);
            setProgress(0);
            setError(null);
          }
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;
        } else if (e.data.type === "ERROR") {
          console.error("MC worker error:", e.data.message);
          worker.onmessage = null;
          worker.onerror = null;
          if (mountedRef.current) {
            setRunning(false);
            setProgress(0);
            setError(e.data.message);
          }
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;
        } else {
          console.error("MC worker malformed response:", e.data);
          worker.onmessage = null;
          worker.onerror = null;
          if (mountedRef.current) {
            setRunning(false);
            setProgress(0);
            setError("Monte Carlo worker returned an invalid response.");
          }
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;
        }
      };

      worker.onerror = (err) => {
        if (workerRef.current !== worker) return;
        console.error("MC worker unhandled error:", err);
        worker.onmessage = null;
        worker.onerror = null;
        if (mountedRef.current) {
          setRunning(false);
          setProgress(0);
          setError(err.message || "Monte Carlo worker failed.");
        }
        worker.terminate();
        if (workerRef.current === worker) workerRef.current = null;
      };

      setRunning(true);
      setProgress(0);
      setError(null);

      const msg: McWorkerRequest = {
        type: "MONTE_CARLO",
        scenario,
        enabledKeys,
        ranges,
        iterations,
      };
      workerRef.current = worker;
      worker.postMessage(msg);
    },
    [],
  );

  const terminate = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.onmessage = null;
      workerRef.current.onerror = null;
      workerRef.current.terminate();
    }
    workerRef.current = null;
    if (mountedRef.current) {
      setRunning(false);
      setProgress(0);
    }
  }, []);

  return { run, running, progress, error, terminate };
}

/* ── Excel worker hook ── */

export interface UseExcelWorkerReturn {
  /** Generate a workbook buffer (skipping charts) in the worker. */
  run: (
    scenario: unknown,
    result: unknown,
    allScenarios: unknown,
    allResults: unknown,
    onReady: (buffer: ArrayBuffer) => void | Promise<void>,
    onError?: (message: string) => void,
  ) => void;
  /** Whether a build is currently running. */
  running: boolean;
  /** Terminate the worker. */
  terminate: () => void;
}

export function useExcelWorker(): UseExcelWorkerReturn {
  const workerRef = useRef<Worker | null>(null);
  const [running, setRunning] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (workerRef.current) {
        workerRef.current.onmessage = null;
        workerRef.current.onerror = null;
        workerRef.current.terminate();
      }
      workerRef.current = null;
    };
  }, []);

  const run = useCallback(
    (
      scenario: unknown,
      result: unknown,
      allScenarios: unknown,
      allResults: unknown,
      onReady: (buffer: ArrayBuffer) => void | Promise<void>,
      onError?: (message: string) => void,
    ) => {
      if (workerRef.current) {
        workerRef.current.onmessage = null;
        workerRef.current.onerror = null;
        workerRef.current.terminate();
      }

      const worker = new Worker(new URL("./excel.worker.ts", import.meta.url), {
        type: "module",
      });

      worker.onmessage = (e: MessageEvent<ExcelWorkerResponse>) => {
        if (e.data.type === "WORKBOOK_READY") {
          worker.onmessage = null;
          worker.onerror = null;
          Promise.resolve(onReady(e.data.buffer))
            .catch((error: unknown) => {
              const message =
                error instanceof Error ? error.message : String(error);
              console.error("Excel export finalization error:", message);
              if (mountedRef.current) onError?.(message);
            })
            .finally(() => {
              if (mountedRef.current) setRunning(false);
              worker.terminate();
              if (workerRef.current === worker) workerRef.current = null;
            });
        } else if (e.data.type === "ERROR") {
          console.error("Excel worker error:", e.data.message);
          worker.onmessage = null;
          worker.onerror = null;
          if (mountedRef.current) {
            onError?.(e.data.message);
            setRunning(false);
          }
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;
        } else {
          console.error("Excel worker malformed response:", e.data);
          worker.onmessage = null;
          worker.onerror = null;
          if (mountedRef.current) {
            onError?.("Excel worker returned an invalid response.");
            setRunning(false);
          }
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;
        }
      };

      worker.onerror = (err) => {
        console.error("Excel worker unhandled error:", err);
        worker.onmessage = null;
        worker.onerror = null;
        if (mountedRef.current) {
          onError?.(err.message || "Excel worker failed.");
          setRunning(false);
        }
        worker.terminate();
        if (workerRef.current === worker) workerRef.current = null;
      };

      setRunning(true);

      const msg: ExcelWorkerRequest = {
        type: "BUILD_WORKBOOK",
        scenario,
        result,
        allScenarios,
        allResults,
      };
      worker.postMessage(msg);
      workerRef.current = worker;
    },
    [],
  );

  const terminate = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.onmessage = null;
      workerRef.current.onerror = null;
      workerRef.current.terminate();
    }
    workerRef.current = null;
    if (mountedRef.current) setRunning(false);
  }, []);

  return { run, running, terminate };
}
